import { Container, FillGradient, Graphics, Matrix, TilingSprite, type FillStyle, type Texture } from 'pixi.js';
import { COLOURS } from './ui';

/** How wide one repeat of a reel floor is drawn on a board, in pixels. */
export const FLOOR_TILE = 220;

/** How dark the shade over a board's floor is: enough that gold and cream
 *  type with its dark outline reads on every world's floor, the light ones
 *  (sherbet, frost) too. */
export const FLOOR_SHADE = { color: 0x180e04, alpha: 0.62 } as const;

/**
 * A fill that tiles a reel floor at the boards' scale, for painting a floor
 * straight into a shape: a small plate, many of them at once, with no mask.
 * The floor repeats from the shape's own origin.
 */
export function floorFill(texture: Texture): FillStyle {
  const s = FLOOR_TILE / Math.max(1, texture.width);
  return { texture, color: 0xffffff, matrix: new Matrix().scale(s, s), textureSpace: 'global' };
}

/** Vertical wood gradient (light at the top), in the shape's own space. */
export function woodGradient(light: number = COLOURS.woodLight, dark: number = COLOURS.woodDark): FillGradient {
  return new FillGradient({
    type: 'linear',
    start: { x: 0, y: 0 },
    end: { x: 0, y: 1 },
    colorStops: [
      { offset: 0, color: light },
      { offset: 1, color: dark },
    ],
    textureSpace: 'local',
  });
}

/**
 * A board over or around the reels: the slot's own reel floor, faded, with
 * a thin brass edge (producer 2026-10-14: only the reels keep the carved
 * frame; everything else is a board) and no drop shadow (producer
 * 2026-11-03). Size it with {@link resize}; the floor art can arrive later
 * through {@link setFloor}.
 */
export class BoardPanel extends Container {
  private readonly base = new Graphics();
  private floor: TilingSprite | null = null;
  private readonly floorMask = new Graphics();
  private readonly shade = new Graphics();
  private readonly edge = new Graphics();
  private w = 1;
  private h = 1;

  constructor(options: { floor?: Texture | null; radius?: number } = {}) {
    super();
    this.radius = options.radius ?? 14;
    this.addChild(this.base, this.floorMask, this.shade, this.edge);
    if (options.floor) this.setFloor(options.floor);
  }

  readonly radius: number;

  get boardWidth(): number {
    return this.w;
  }

  get boardHeight(): number {
    return this.h;
  }

  /** The floor art under the board, or null before it has any. */
  get floorTexture(): Texture | null {
    return this.floor?.texture ?? null;
  }

  /** Put the slot's reel floor under the board (once; later calls are
   *  ignored unless the texture changes). */
  setFloor(texture: Texture | null): void {
    if (!texture || this.floor?.texture === texture) return;
    this.floor?.destroy();
    this.floor = new TilingSprite({ texture, width: this.w, height: this.h });
    this.floor.mask = this.floorMask;
    this.addChildAt(this.floor, this.getChildIndex(this.floorMask));
    this.redraw();
  }

  resize(width: number, height: number): void {
    this.w = Math.max(1, Math.round(width));
    this.h = Math.max(1, Math.round(height));
    this.redraw();
  }

  private redraw(): void {
    const { w, h, radius: r } = this;
    this.base.clear().roundRect(0, 0, w, h, r).fill({ color: COLOURS.floor });
    // The mask's white shape draws as itself when no floor uses it: only
    // give it one while there is a floor (a board before its art loads
    // was white).
    this.floorMask.clear();
    if (this.floor) this.floorMask.roundRect(0, 0, w, h, r).fill({ color: 0xffffff });
    if (this.floor) {
      this.floor.width = w;
      this.floor.height = h;
      const scale = FLOOR_TILE / Math.max(1, this.floor.texture.width);
      this.floor.tileScale.set(scale, scale);
    }
    this.shade
      .clear()
      .roundRect(0, 0, w, h, r)
      .fill({ color: FLOOR_SHADE.color, alpha: this.floor ? FLOOR_SHADE.alpha : 0 });
    this.edge
      .clear()
      .roundRect(0, 0, w, h, r)
      .stroke({ color: COLOURS.brass, width: 2, alignment: 1 })
      .roundRect(2, 2, w - 4, h - 4, Math.max(2, r - 2))
      .stroke({ color: COLOURS.brassLight, alpha: 0.18, width: 1, alignment: 1 });
  }
}
